/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-function-dialog.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@progress/kendo-react-inputs';

import { useBoolean, useTranslation } from 'hooks';
import { SAVE_NAMED_ITEM } from 'queries/common';

import 'views/components/admin/studies/function-dialogs/styles.scss';
import { StudyFields, NamedItemFields } from 'queries/generated/fragments.generated';
import { NamedItemType } from 'types/graphql-global-types';
import NotificationService from 'utils/notification';
import { SaveButton } from 'views/components/core/buttons';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';

const EditFunctionDialog = ({
    study,
    open,
    onClose,
    currentFun,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
    currentFun: NamedItemFields;
}) => {
    const { t } = useTranslation();
    const active = useBoolean(false);
    const disabledSave = useBoolean(true);
    const [saveNamedItem] = useMutation(SAVE_NAMED_ITEM);

    useEffect(() => {
        active.setValue(currentFun?.active || false);
    }, [currentFun]);

    const close = (action: string | boolean | undefined) => {
        disabledSave.setTrue();
        onClose(action);
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSave.setFalse();
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:functionSuccessfulUpdated', { id: currentFun.recordId }));
        onClose();
    };

    const onSubmit = () => {
        disabledSave.setTrue();
        saveNamedItem({
            variables: {
                id: currentFun.id,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
                name: currentFun.name,
                active: active.value,
                studyId: study.id,
            },
        })
            .then(() => {
                successRequest();
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog
            onClose={() => close(false)}
            aria-labelledby="edit-function"
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
            className="custom-width-popup"
        >
            <DialogTitle id="edit-function">
                {t('admin:editFunctionNumber', { id: currentFun?.recordId })}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close(false)}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="edit-function-dialog-content">
                <Checkbox
                    rounded={null}
                    className="cell-checkbox no-full-width flex-start no-border"
                    checked={active.value}
                    onChange={() => {
                        disabledSave.setFalse();
                        active.setValue(!active.value);
                    }}
                    label={t('active')}
                    data-testid="active-checkbox"
                    size="medium"
                />
                <div className="name-wrapper">
                    <p>
                        <span>{`${t('name')}: `}</span>
                        {currentFun?.name}
                    </p>
                </div>
            </DialogContent>

            <DialogActions className="actions-info-wrapper-edit-function dialog-actions">
                <ModalUpdatedInfo item={currentFun} />
                <SaveButton
                    style={{ minWidth: 0 }}
                    id="edit-function-save"
                    disabled={disabledSave.value}
                    text="update"
                    onClick={onSubmit}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditFunctionDialog;
